<script lang="ts">
  import { Dialog } from 'bits-ui'

  interface Props {
    open: boolean
    videoId: string | null
    videoTitle: string
    displayTime: number
    onClose: () => void
    onLoad: (videoId: string, startTime?: number) => void
  }

  let { open, videoId, videoTitle, displayTime, onClose, onLoad }: Props = $props()

  let urlInput = $state('')
  let urlInputEl: HTMLInputElement
  let originalUrl = $state('')
  let copySuccess = $state(false)

  $effect(() => {
    if (open) {
      if (videoId) {
        const url = displayTime > 0
          ? `https://youtu.be/${videoId}?t=${Math.floor(displayTime)}`
          : `https://youtu.be/${videoId}`
        urlInput = url
        originalUrl = url
      } else {
        urlInput = ''
        originalUrl = ''
      }
      copySuccess = false
    }
  })

  function parseYouTubeUrl(input: string): { videoId: string, startTime?: number } | null {
    const trimmed = input.trim()
    if (!trimmed) return null

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ]

    for (const pattern of patterns) {
      const match = trimmed.match(pattern)
      if (match) {
        const result: { videoId: string, startTime?: number } = { videoId: match[1] }
        const urlMatch = trimmed.match(/[?&]t=(\d+)s?/)
        if (urlMatch) {
          result.startTime = parseInt(urlMatch[1], 10)
        }
        return result
      }
    }
    return null
  }

  function formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  let urlChanged = $derived(urlInput.trim() !== originalUrl)
  let urlValid = $derived(parseYouTubeUrl(urlInput) !== null)

  function handleLoad() {
    const parsed = parseYouTubeUrl(urlInput)
    if (!parsed) return
    onLoad(parsed.videoId, parsed.startTime)
  }

  function handleCopy() {
    navigator.clipboard.writeText(urlInput)
    copySuccess = true
  }
</script>

<Dialog.Root bind:open onOpenChange={(o) => !o && onClose()}>
  <Dialog.Portal>
    <Dialog.Overlay class="fixed inset-0 bg-black/70 z-50" />
    <Dialog.Content
      class="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[500px] rounded-lg bg-surface-2 p-8"
      onOpenAutoFocus={(e) => {
        e.preventDefault()
        requestAnimationFrame(() => urlInputEl?.focus())
      }}
    >
      <Dialog.Close class="absolute top-2 right-3 text-2xl text-text-muted hover:text-text-primary cursor-pointer bg-transparent border-none">
        &times;
      </Dialog.Close>

      {#if videoTitle}
        <Dialog.Title class="m-0 mb-6 text-lg text-text-primary font-medium leading-relaxed text-left">
          {videoTitle}
        </Dialog.Title>
      {/if}

      <div class="mb-4">
        {#if !urlChanged && displayTime > 0}
          <label class="block text-xs text-text-muted mb-1.5 text-left">At {formatTime(displayTime)}</label>
        {:else if urlChanged || !videoId}
          <label class="block text-xs text-text-muted mb-1.5 text-left">Paste a YouTube URL to load</label>
        {/if}
        <div class="flex gap-2">
          <input
            type="text"
            bind:value={urlInput}
            bind:this={urlInputEl}
            placeholder="https://youtu.be/..."
            class="flex-1 py-3 px-4 text-sm bg-surface-3 border border-border-default rounded text-text-primary focus:outline-none focus:border-accent-primary"
          />
          {#if urlChanged || !videoId}
            <button
              onclick={handleLoad}
              disabled={!urlValid}
              class="py-3 px-5 text-sm bg-accent-success border-none rounded text-white cursor-pointer whitespace-nowrap min-w-[70px] hover:enabled:bg-green-600 disabled:bg-surface-4 disabled:text-text-muted disabled:cursor-not-allowed"
            >
              Load
            </button>
          {:else}
            <button
              onclick={handleCopy}
              class="py-3 px-5 text-sm border-none rounded text-white cursor-pointer whitespace-nowrap min-w-[70px] {copySuccess ? 'bg-accent-success' : 'bg-accent-primary hover:bg-accent-hover'}"
            >
              {copySuccess ? '✓' : 'Copy'}
            </button>
          {/if}
        </div>
      </div>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>
